import { DefaultButton, Text } from "@fluentui/react";
import { makeStyles, shorthands, Tooltip } from "@fluentui/react-components";
import { ArrowSyncRegular, History32Filled } from "@fluentui/react-icons";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import CreateIcon from "@mui/icons-material/Create";
import SearchIcon from "@mui/icons-material/Search";
import { Alert, Box, Button, Divider, Drawer, IconButton, InputAdornment, Portal, Skeleton, Snackbar, styled, Tab, TextField } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { escapeHtml, highlightTextAsHtml, saveUserDetails, SessionInfo, skipUnwantedTag, YearData } from "../../api";
import { userInfo } from "../../authConfig";
import i18next from "../../i18n/i18n";
import { QuestionInputReducerAction } from "../../Store/QuestionInputReducer";
import { SessionActionsMenu } from "./SessionActionsMenu";
import { SourcePage } from "./SessionNotFound";
import { RootState } from "../../Store";

interface Props {
    nextDate: string;
    sourcePage: string;
    isLoading: boolean;
    currentIndex: string;
    lastSessionRef?: any;
    lastActiveIndexRef?: any;
    lastActiveSessionRef?: any;
    todaySessionsRef: SessionInfo[];
    last7SessionsRef: SessionInfo[];
    last30SessionsRef: SessionInfo[];
    monthlySessions: YearData[];
    loadChatData: (session_id: string) => void;
    setCurrentIndex: (session_id: string) => void;
    loadSessionsData: (searchTerm: string, isNextLoadClicked: boolean) => void;
    isOnSave?: boolean;
    clearChat: () => void;
    isSessionLoading: boolean;
    isAdmin: boolean;
    clearSearchChat: () => void;
    setTodaySessions: (sessions: any) => void;
    setLast7DaySessions: (sessions: any) => void;
    setLast30DaySessions: (sessions: any) => void;
    refreshSessionsAfterDelete?: (sessionId: string | undefined) => Promise<void>;
}

type PeriodKey = "today" | "last7days" | "last30days";

const monthNames = {
    Jan: "January",
    Feb: "February",
    Mar: "March",
    Apr: "April",
    May: "May",
    Jun: "June",
    Jul: "July",
    Aug: "August",
    Sep: "September",
    Oct: "October",
    Nov: "November",
    Dec: "December"
};

const useStyles = makeStyles({
    root: {
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        ...shorthands.padding("10px", "0px"),
        paddingTop: "0px",
        paddingBottom: "0px",
        paddingLeft: "30px",
        paddingRight: "20px"
    },
    tabStyle: {
        color: "white",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        lineHeight: 2,
        fontSize: "15px",
        cursor: "pointer",
        paddingTop: "0px",
        paddingBottom: "0px",
        paddingLeft: "0px",
        paddingRight: "0px",
        textTransform: "none",
        ":active": {
            fontWeight: "bold"
        },
        ":selected": {
            fontWeight: "bold"
        },
        ":hover": {
            fontWeight: "bold"
        },
        ":target": {
            fontWeight: "bold"
        }
    },
    year: { minHeight: "40px !important", fontSize: "25px" },
    month: { minHeight: "40px !important", fontSize: "20px" }
});

const groupSessionsByYearAndMonth = (data: YearData[]): any => {
    const grouped: any = {};

    data?.forEach(yearData => {
        const { year, sessions } = yearData;

        if (!grouped[year]) {
            grouped[year] = {};
        }

        sessions?.forEach(session => {
            let { monthName, records } = session;
            monthName = getTranslatedMonth(monthName);
            if (!grouped[year][monthName]) {
                grouped[year][monthName] = [];
            }
            records?.forEach((record: any) => {
                const isDuplicate = grouped[year][monthName].some(
                    (existingRecord: any) => existingRecord.sessionId === record.sessionId && existingRecord.conversation_date === record.conversation_date
                );

                if (!isDuplicate) {
                    grouped[year][monthName].push(record);
                }
            });
        });
    });
    return grouped;
};

const getTranslatedMonth = (month: string) => {
    switch (month) {
        case monthNames.Jan:
            return i18next.t("monthNames.Jan");
        case monthNames.Feb:
            return i18next.t("monthNames.Feb");
        case monthNames.Mar:
            return i18next.t("monthNames.Mar");
        case monthNames.Apr:
            return i18next.t("monthNames.Apr");
        case monthNames.May:
            return i18next.t("monthNames.May");
        case monthNames.Jun:
            return i18next.t("monthNames.Jun");
        case monthNames.Jul:
            return i18next.t("monthNames.Jul");
        case monthNames.Aug:
            return i18next.t("monthNames.Aug");
        case monthNames.Sep:
            return i18next.t("monthNames.Sep");
        case monthNames.Oct:
            return i18next.t("monthNames.Oct");
        case monthNames.Nov:
            return i18next.t("monthNames.Nov");
        case monthNames.Dec:
            return i18next.t("monthNames.Dec");
        default:
            return month;
    }
};

export const Sidebar = (props: Props) => {
    const tabstyles = useStyles();
    const [showGroupedSessions, setShowGroupedSessions] = useState(false);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [monthGroupedSessions, setMonthGroupedSessions] = useState<any>();
    const [isSidebarOpen, setIsSidebarOpen] = useState(userInfo?.isSidebarOpen ? (userInfo?.isSidebarOpen === "true" ? true : false) : true);
    const dispatch = useDispatch();
    const fileUploadOrDeleteAPITriggerActive = useSelector((store: RootState) => store.questionInput.fileUploadOrDeleteAPITriggerActive);
    const [searchTerm, setSearchTerm] = useState("");
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
    const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);

    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Snackbar state for sidebar notifications
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "info" | "warning";
    }>({
        open: false,
        message: "",
        severity: "success"
    });

    const showSidebarToast = useCallback((message: string, severity: "success" | "error" | "info" | "warning" = "success") => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    }, []);

    const handleSnackbarClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);
    const periodMap: Record<PeriodKey, { label: string; data: SessionInfo[] }> = {
        today: {
            label: i18next.t("chat.today"),
            data: props.todaySessionsRef
        },
        last7days: {
            label: i18next.t("chat.last7days"),
            data: props.last7SessionsRef
        },
        last30days: {
            label: i18next.t("chat.last30days"),
            data: props.last30SessionsRef
        }
    };

    const calculateDrawerWidth = () => {
        const viewportWidth = window.innerWidth;
        return viewportWidth <= 1400 ? "40vh" : "30vh";
    };

    const DrawerHeader = styled("div")(({ theme }) => ({
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0, 1),
        ...theme.mixins.toolbar,
        justifyContent: "flex-start"
    }));

    const handleOnClickOfSessionId = (item: SessionInfo, activeSession: any) => {
        const urlParams = new URLSearchParams();
        urlParams.append("SessionId", item.session_id ?? "");
        props.setCurrentIndex(item.session_id ?? "");
        props.loadChatData(item.session_id ?? "");
        setActiveSessionInfo(item, activeSession);
    };

    const setActiveSessionInfo = (item: SessionInfo, activeSession: any) => {
        if (props.sourcePage === "chat") {
            props.lastActiveIndexRef.current = item.group;
            props.lastSessionRef.current = item;
            props.lastActiveSessionRef.current = activeSession;
            sessionStorage.setItem("sessionId", item.session_id ?? "");
        }
    };

    // Use a Set for O(1) session ID lookup
    const allSessionIdsSet = useMemo(() => {
        return new Set([
            ...props.todaySessionsRef.map(s => s.session_id),
            ...props.last7SessionsRef.map(s => s.session_id),
            ...props.last30SessionsRef.map(s => s.session_id),
            ...props.monthlySessions.flatMap(yearData => yearData.sessions.flatMap(monthData => monthData.records.map((r: any) => r.session_id)))
        ]);
    }, [props.todaySessionsRef, props.last7SessionsRef, props.last30SessionsRef, props.monthlySessions]);

    useEffect(() => {
        const hasData =
            props.todaySessionsRef.length > 0 || props.last7SessionsRef.length > 0 || props.last30SessionsRef.length > 0 || props.monthlySessions.length > 0;
        if (hasData) {
            const grouped = groupSessionsByYearAndMonth([...props.monthlySessions]);
            setMonthGroupedSessions(grouped);
        } else {
            setMonthGroupedSessions([]);
        }

        // After session list changes, if currentIndex is not present in any session, clear selection
        if (props.currentIndex && !allSessionIdsSet.has(props.currentIndex)) {
            props.setCurrentIndex("");
        }
    }, [props.todaySessionsRef, props.last7SessionsRef, props.last30SessionsRef, props.monthlySessions, deletingSessionId, allSessionIdsSet]);

    useEffect(() => {
        if (window.location.pathname === "/assistant/chat") {
            setTimeout(() => {
                setIsLoading(false);
            }, 1000);
        } else {
            setIsLoading(false);
        }
    }, [props.todaySessionsRef, props.last7SessionsRef, props.last30SessionsRef, props.monthlySessions]);

    const loadMoreSessions = () => {
        setIsLoading(true);
        props.loadSessionsData("", true);
        setShowGroupedSessions(true);
    };

    const EmptySkeletons = () => {
        return (
            <div className={tabstyles.root}>
                <Box sx={{ width: "100%" }}>
                    <Skeleton animation="wave" sx={{ width: "100%" }} />
                    <Skeleton animation="wave" sx={{ width: "90%" }} />
                    <Skeleton animation="wave" sx={{ width: "80%" }} />
                    <Skeleton animation="wave" sx={{ width: "70%" }} />
                </Box>
            </div>
        );
    };

    const handleDrawerOpen = () => {
        const newisSidebarOpen = !isSidebarOpen;
        dispatch(QuestionInputReducerAction.updateIsSidebarOpen(newisSidebarOpen));
        setIsSidebarOpen(newisSidebarOpen);
        userInfo.isSidebarOpen = newisSidebarOpen.toString();
        saveUserSettings({ isSidebarOpen: newisSidebarOpen.toString() });
    };

    const saveUserSettings = async (settings: object, reloadNeeded?: boolean) => {
        try {
            await saveUserDetails(settings)
                .then(response => {
                    return response.json();
                })
                .then(() => {
                    if (reloadNeeded) {
                        window.location.reload();
                    }
                })
                .catch(error => {
                    console.error("Error saving user data:", error);
                });
        } catch (error) {
            console.error("Error saving user data:", error);
        }
    };

    const iconButtonStyles = {
        width: "100%",
        height: "40px",
        // Match the rounded container so the hover/ripple shade doesn't draw a rectangular box inside the pill.
        borderRadius: "calc(1.5rem - 4px)"
    };

    const iconStyles = {
        background: "none",
        width: "40px",
        height: "40px",
        color: "#5A5A5A"
    };

    const toggleButtonContainer: React.CSSProperties = {
        display: "flex",
        justifyContent: "center",
        border: "2px solid rgba(0,0,0,.1)",
        borderRadius: "1.5rem",
        padding: "2px",
        margin: "0px 8px 8px 8px",
        backgroundColor: "white",
        overflow: "hidden",
        cursor: fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer"
    };

    const textStyles = {
        padding: "0.25rem 0.5rem",
        fontSize: "16px",
        fontWeight: "bold",
        width: "150px",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        color: "#5A5A5A"
    };

    useEffect(() => {
        if (props.isSessionLoading) {
            setIsLoading(true);
        }
    }, [props.isSessionLoading]);

    const debounce = (func: (...args: any[]) => void, delay = 100) => {
        let timeout: ReturnType<typeof setTimeout>;
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), delay);
        };
    };

    const stableLoadSessionsData = useCallback(
        (searchTerm: string, isNextLoadClicked: boolean) => {
            setIsLoading(true);
            props.loadSessionsData(searchTerm, isNextLoadClicked);
        },
        [props.loadSessionsData]
    );

    const debouncedSearch = useMemo(
        () =>
            debounce((term: string) => {
                setIsSearchActive(true);
                dispatch(QuestionInputReducerAction.updateIsSearchActive(true));
                stableLoadSessionsData(term, false);
            }, 100),
        [stableLoadSessionsData]
    );

    const handleSearch = () => {
        debouncedSearch(searchTerm);
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        setIsSearchActive(false);
        props.clearSearchChat();
        dispatch(QuestionInputReducerAction.updateIsSearchActive(false));
    };

    const tabTextStyles = (item: SessionInfo): React.CSSProperties => ({
        fontWeight: props.currentIndex === item.session_id ? "bold" : "normal",
        background: props.currentIndex === item.session_id ? `linear-gradient(90deg, none, none 84%, transparent 89%, transparent)` : "none",
        borderRadius: "10px",
        backgroundColor: props.currentIndex === item.session_id ? "#b4b4b4" : "transparent",
        padding: "4px 8px",
        color: "black",
        display: "block",
        width: "100%",
        textAlign: "left",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis"
    });

    const tabAttributes = (item: SessionInfo, data: SessionInfo[]) => ({
        className: tabstyles.tabStyle,
        key: item.session_id,
        value: item.session_id,
        onClick: (event: React.SyntheticEvent) => {
            // Don't trigger if the click came from inside a Dialog or Menu or if any session is deleting or API trigger is active
            if (deletingSessionId !== null || fileUploadOrDeleteAPITriggerActive?.triggerActive) return;
            const target = event.target as HTMLElement;
            if (target.closest('[role="dialog"]') || target.closest('[role="menu"]')) {
                return;
            }
            handleOnClickOfSessionId(item, data);
        },
        onMouseEnter: () => deletingSessionId === null && setHoveredSessionId(item.session_id ?? null),
        onMouseLeave: () => setHoveredSessionId(null),
        disabled: props.isLoading || deletingSessionId !== null || fileUploadOrDeleteAPITriggerActive?.triggerActive,
        label: (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    fontWeight: props.currentIndex === item.session_id ? "bold" : "normal",
                    background: props.currentIndex === item.session_id ? `linear-gradient(90deg, none, none 84%, transparent 89%, transparent)` : "none",
                    borderRadius: "10px",
                    backgroundColor: props.currentIndex === item.session_id ? "#b4b4b4" : "transparent",
                    padding: "4px 8px",
                    color: "black",
                    position: "relative",
                    boxSizing: "border-box",
                    cursor: fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer"
                }}
            >
                <>
                    <div
                        title={props.isAdmin || userInfo.isWap === "True" || userInfo.isWap === "true" ? item.title : item.chat_summary_title}
                        style={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                            paddingRight: "32px"
                        }}
                        dangerouslySetInnerHTML={{
                            __html: skipUnwantedTag(highlightTextAsHtml(escapeHtml(item.chat_summary_title ?? item.title ?? ""), searchTerm))
                        }}
                    />
                    <SessionActionsMenu
                        session={item}
                        isVisible={hoveredSessionId === item.session_id || props.currentIndex === item.session_id}
                        onDeleteSuccess={async () => {
                            if (props.currentIndex === item.session_id) {
                                props.setCurrentIndex("");
                                props.clearChat();
                            }
                            setHoveredSessionId(null);
                            await props.refreshSessionsAfterDelete?.(item.session_id);
                            setDeletingSessionId(null);
                        }}
                        showToast={showSidebarToast}
                        isDeleting={deletingSessionId === item.session_id}
                        setDeletingSessionId={setDeletingSessionId}
                        sourcePage={props.sourcePage as SourcePage}
                    />
                </>
            </div>
        )
    });

    const tabSXValues = {
        width: "100%",
        maxWidth: "100%",
        textAlign: "left",
        alignItems: "flex-start",
        justifyContent: "flex-start"
    };

    const iconButtnStyles = { margin: isSidebarOpen ? undefined : "0", padding: isSidebarOpen ? undefined : "1px" };

    return (
        <>
            <Drawer
                ref={sidebarRef}
                id="sidebar"
                open={true}
                variant="persistent"
                anchor="left"
                sx={{
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: isSidebarOpen ? calculateDrawerWidth() : "70px",
                        boxSizing: "border-box",
                        background: "#e3e3e3",
                        fontWeight: 650,
                        transition: "all 0.5s",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        height: innerHeight - 55,
                        zIndex: 0
                    }
                }}
                ModalProps={{ keepMounted: false }}
            >
                <Box sx={{ position: "sticky", top: 0, background: "#e3e3e3", zIndex: 1, paddingBottom: "8px", margin: isSidebarOpen ? "8px 8px" : "8px 0px" }}>
                    {props.sourcePage === "chat" && (
                        <Tooltip
                            content={{
                                children: i18next.t("clearChat.newChat"),
                                style: {
                                    backgroundColor: "white"
                                }
                            }}
                            relationship="label"
                        >
                            <div id="historyToggleButton" style={toggleButtonContainer}>
                                <Button
                                    onClick={() => {
                                        props.clearChat();
                                    }}
                                    disabled={fileUploadOrDeleteAPITriggerActive?.triggerActive || props.isOnSave || deletingSessionId !== null}
                                    sx={iconButtonStyles}
                                >
                                    <CreateIcon sx={iconStyles} />
                                    {isSidebarOpen && <Text style={textStyles}>{i18next.t("clearChat.newChat")}</Text>}
                                </Button>
                            </div>
                        </Tooltip>
                    )}
                    <Tooltip
                        content={{
                            children: i18next.t(isSidebarOpen ? "layout.closeDrawer" : "layout.openDrawer"),
                            style: {
                                backgroundColor: "white"
                            }
                        }}
                        relationship="label"
                    >
                        <div id="historyToggleButton" style={toggleButtonContainer}>
                            <Button
                                onClick={handleDrawerOpen}
                                disabled={fileUploadOrDeleteAPITriggerActive?.triggerActive || props.isOnSave}
                                sx={iconButtonStyles}
                            >
                                {isSidebarOpen ? <ArrowBackIosNewIcon sx={iconStyles} /> : <History32Filled style={iconStyles} />}
                                {isSidebarOpen && <Text style={textStyles}>{i18next.t("layout.closeDrawer")}</Text>}
                            </Button>
                        </div>
                    </Tooltip>
                    {props.sourcePage !== "feedback" && (
                        <TextField
                            label={isSidebarOpen ? i18next.t("layout.search_sessions") : ""}
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            title={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === "Enter" && searchTerm !== "" && !fileUploadOrDeleteAPITriggerActive?.triggerActive) {
                                    handleSearch();
                                }
                            }}
                            style={{
                                borderRadius: "10px",
                                border: "1px solid",
                                color: "white",
                                cursor: fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer",
                                backgroundColor: "white",
                                width: "100%",
                                textOverflow: "ellipsis",
                                opacity: fileUploadOrDeleteAPITriggerActive?.triggerActive ? 0.6 : 1
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {isSearchActive && (
                                            <IconButton
                                                onClick={handleClearSearch}
                                                edge="end"
                                                sx={{
                                                    ...iconButtnStyles,
                                                    cursor: fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer"
                                                }}
                                                disabled={fileUploadOrDeleteAPITriggerActive?.triggerActive}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        {searchTerm !== "" && (
                                            <IconButton
                                                onClick={handleSearch}
                                                edge="end"
                                                sx={{
                                                    ...iconButtnStyles,
                                                    cursor: fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer"
                                                }}
                                                disabled={fileUploadOrDeleteAPITriggerActive?.triggerActive}
                                            >
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </InputAdornment>
                                )
                            }}
                            disabled={fileUploadOrDeleteAPITriggerActive?.triggerActive}
                        />
                    )}
                    <Divider />
                </Box>

                {isSidebarOpen && (
                    <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
                        {Number(props.todaySessionsRef.length) === 0 &&
                        Number(props.last7SessionsRef.length) === 0 &&
                        Number(props.last30SessionsRef.length) === 0 &&
                        Object.keys(monthGroupedSessions ?? {}).length === 0 ? (
                            <div
                                style={{
                                    boxSizing: "border-box",
                                    background: "#e3e3e3",
                                    position: "relative",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    top: "1px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: innerHeight - 200,
                                    transition: "all 0.5s",
                                    zIndex: 0,
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                <p>{i18next.t("layout.NoDataAvailable")}</p>
                            </div>
                        ) : (
                            <>
                                {(["today", "last7days", "last30days"] as PeriodKey[]).map((periodKey, i) => {
                                    const { label, data } = periodMap[periodKey];
                                    return (props.isSessionLoading || isLoading) && window.location.pathname === "/assistant/chat" ? (
                                        <Box key={periodKey}>
                                            <DrawerHeader>{label}</DrawerHeader>
                                            <EmptySkeletons />
                                        </Box>
                                    ) : data?.length > 0 ? (
                                        <React.Fragment key={periodKey}>
                                            <DrawerHeader>{label}</DrawerHeader>
                                            <div className={tabstyles.root}>
                                                {data.map((item, index) => {
                                                    if (props.currentIndex === item.session_id) {
                                                        setActiveSessionInfo(item, data);
                                                    }
                                                    return <Tab {...tabAttributes(item, data)} sx={tabSXValues} />;
                                                })}
                                            </div>
                                        </React.Fragment>
                                    ) : null;
                                })}

                                {((props.sourcePage === "chat" && showGroupedSessions) ||
                                    props.sourcePage === "feedback" ||
                                    (props.sourcePage === "chat" &&
                                        !props.todaySessionsRef.length &&
                                        !props.last7SessionsRef.length &&
                                        !props.last30SessionsRef.length) ||
                                    isSearchActive) &&
                                    (props.isSessionLoading || isLoading ? (
                                        <Box sx={{ height: window.innerHeight }}>
                                            <DrawerHeader>{i18next.t("chat.year")}</DrawerHeader>
                                            <div className={tabstyles.root}>
                                                {Array.from({ length: Math.round(window.innerHeight / 28) }, (_, i) => (
                                                    <Tab
                                                        className={tabstyles.tabStyle}
                                                        key={`skeleton_${i}`}
                                                        value={`skeleton_${i}`}
                                                        disabled
                                                        label={<Skeleton animation="wave" sx={{ width: calculateDrawerWidth() }} />}
                                                    />
                                                ))}
                                            </div>
                                        </Box>
                                    ) : Object.keys(monthGroupedSessions ?? {}).length > 0 ? (
                                        <>
                                            {Object.keys(monthGroupedSessions)
                                                .sort()
                                                .reverse()
                                                .map(year => {
                                                    const months = Object.keys(monthGroupedSessions[year]);
                                                    return (
                                                        <div key={year}>
                                                            {months?.length > 0 && year !== new Date().getFullYear().toString() && (
                                                                <>
                                                                    <br />
                                                                    <DrawerHeader className={tabstyles.year} style={{ textDecoration: "underline" }}>
                                                                        {year}
                                                                    </DrawerHeader>
                                                                </>
                                                            )}
                                                            {months?.map(month => {
                                                                const monthData = monthGroupedSessions[year][month];
                                                                if (!monthData || monthData.length === 0) return null;
                                                                return (
                                                                    <div key={`${year}-${month}`}>
                                                                        <DrawerHeader className={tabstyles.month}>{month}</DrawerHeader>
                                                                        <div className={tabstyles.root}>
                                                                            {monthData.map((item: any, index: number) => {
                                                                                if (props.currentIndex === item.session_id) {
                                                                                    setActiveSessionInfo(item, monthData);
                                                                                }
                                                                                return <Tab {...tabAttributes(item, monthData)} sx={tabSXValues} />;
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })}
                                        </>
                                    ) : null)}

                                {!isSearchActive && props.nextDate !== "" && (
                                    <div style={{ display: "flex", justifyContent: "center", padding: "10px" }}>
                                        <DefaultButton
                                            style={{
                                                padding: "10px 20px",
                                                backgroundColor: "#7376e1",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "10px",
                                                cursor: "pointer",
                                                width: "auto"
                                            }}
                                            onClick={loadMoreSessions}
                                        >
                                            {isLoading ? (
                                                <CircularProgress style={{ width: "20px", height: "20px", color: "white" }} />
                                            ) : (
                                                <ArrowSyncRegular style={{ marginRight: "5px" }} />
                                            )}
                                        </DefaultButton>
                                    </div>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </Drawer>

            {/* Sidebar Snackbar notifications - rendered outside Drawer using Portal */}
            {snackbar.open && (
                <Portal>
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={handleSnackbarClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        sx={{
                            zIndex: 10000, // Higher than drawer z-index
                            "& .MuiSnackbarContent-root": {
                                minWidth: "300px",
                                maxWidth: "600px" // Allow wider for long messages
                            }
                        }}
                    >
                        <Alert
                            onClose={handleSnackbarClose}
                            severity={snackbar.severity}
                            variant="filled"
                            sx={{
                                width: "100%",
                                minWidth: "300px",
                                maxWidth: "600px", // Allow wider content
                                fontSize: "14px",
                                fontWeight: "500",
                                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.25)",
                                borderRadius: "8px",
                                "& .MuiAlert-message": {
                                    padding: "8px 0",
                                    lineHeight: "1.4",
                                    wordBreak: "break-word", // Handle long text
                                    maxWidth: "500px" // Prevent excessive width
                                },
                                "& .MuiAlert-action": {
                                    paddingTop: "4px"
                                }
                            }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Portal>
            )}
        </>
    );
};
